// =============================================================================
// PPIProvaPublica — a prova que o candidato abre pelo QR da sala
// =============================================================================
// Dois fluxos, MESMA tela:
//   • por VAGA     (rota /ppi-prova/:token)           — 1 QR por cargo (legado)
//   • por SALA     (rota /ppi-prova-processo/:tokens) — 1 QR só para a sala; o CPF
//     resolve a trilha do candidato (porProcesso=true). Desde 2026-08-24 o segmento
//     pode carregar VÁRIOS processos aplicados no mesmo dia ("<tokenA>-<tokenB>"),
//     e o link de um processo só é o caso de um item.
// Sem login. Só abre para quem: é candidato + foi APROVADO na triagem + está com
// PRESENÇA marcada + AINDA NÃO entregou (e, no fluxo por processo, a prova não foi
// ENCERRADA pelo aplicador).
//
// A ordem das questões e das alternativas vem EMBARALHADA pelo servidor (anti-cola)
// e é estável para a pessoa — recarregar não remexe a prova.
//
// AUTOSAVE (2026-07-23): cada resposta é gravada no servidor conforme o candidato
// marca (sem entregar). É o que permite o "Encerrar prova" aproveitar o parcial de
// quem não enviou.

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, AlertCircle } from 'lucide-react';

const COR = '#0284c7';   // azul do módulo Treinamento (o PPI migrou do RH em 2026-07-15)

const mascararCPF = (v) => String(v).replace(/\D/g, '').slice(0, 11)
    .replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2');

function PPIProvaPublica({ porProcesso = false }) {
    const { token } = useParams();
    const API = '/api/treinamento/ppi/prova';
    // Endpoints por fluxo. O autosave e a entrega final por processo são por SESSÃO
    // (não levam token na URL) — o servidor identifica o candidato pela sessão.
    const urlCabecalho = porProcesso ? `${API}/public/proc/${token}` : `${API}/public/${token}`;
    const urlIniciar = porProcesso ? `${API}/public/proc/${token}/iniciar` : `${API}/public/${token}/iniciar`;
    const urlResponder = porProcesso ? `${API}/public/responder` : `${API}/public/${token}/responder`;
    const [encerrada, setEncerrada] = useState(false);   // prova encerrada pelo aplicador
    const [cabecalho, setCabecalho] = useState(null);
    const [erroCarga, setErroCarga] = useState('');

    const [cpf, setCpf] = useState('');
    const [entrando, setEntrando] = useState(false);
    const [erro, setErro] = useState('');

    const [sessao, setSessao] = useState(null);
    const [nome, setNome] = useState('');
    // A prova vem como LISTA ÚNICA, SEM área (questões de todas as áreas misturadas
    // e embaralhadas por candidato) — o candidato não deve inferir o assunto.
    const [questoes, setQuestoes] = useState([]);
    const [respostas, setRespostas] = useState({});   // { [idProvaQuestao]: idAlternativa }
    const [enviando, setEnviando] = useState(false);
    const [entregue, setEntregue] = useState(false);

    // Todas as questões, na ordem em que serão exibidas (para numerar e contar).
    const todasQuestoes = questoes;

    useEffect(() => {
        (async () => {
            try {
                const r = await fetch(urlCabecalho);
                if (!r.ok) {
                    // 410 = a prova por processo foi encerrada pelo aplicador.
                    if (r.status === 410) { setEncerrada(true); return; }
                    throw new Error('Prova não disponível.');
                }
                setCabecalho(await r.json());
            } catch (e) { setErroCarga(e.message); }
        })();
    }, [urlCabecalho]);

    const iniciar = async (e) => {
        e.preventDefault();
        setErro(''); setEntrando(true);
        try {
            const r = await fetch(urlIniciar, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ cpf: cpf.replace(/\D/g, '') }),
            });
            const d = await r.json();
            if (!r.ok) {
                if (r.status === 410) { setEncerrada(true); return; }
                throw new Error(d.message);
            }
            setSessao(d.sessao); setNome(d.nome); setQuestoes(d.questoes || []);
        } catch (err) { setErro(err.message); }
        finally { setEntrando(false); }
    };

    // Grava UMA resposta no servidor conforme o candidato marca (não entrega).
    // Fire-and-forget: um erro de rede aqui não pode travar a prova — a entrega
    // final reenvia tudo. Se o servidor disser que encerrou, a tela reflete isso.
    const autosalvar = (idProvaQuestao, idAlternativa) => {
        if (!sessao) return;
        fetch(`${API}/public/autosave`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sessao, idProvaQuestao, idAlternativa }),
        }).then((r) => { if (r.status === 409 || r.status === 410) setEncerrada(true); })
            .catch(() => { /* rede instável não pode derrubar a prova */ });
    };

    const marcar = (idQuestao, idAlternativa) => {
        setRespostas((r) => ({ ...r, [idQuestao]: idAlternativa }));
        autosalvar(idQuestao, idAlternativa);
    };

    const enviar = async () => {
        const faltando = todasQuestoes.filter((q) => !respostas[q.id]).length;
        if (faltando > 0 && !window.confirm(`Você deixou ${faltando} questão(ões) em branco.\n\nEntregar assim mesmo? Não dá para refazer.`)) return;
        if (faltando === 0 && !window.confirm('Entregar a prova? Não dá para refazer.')) return;

        setEnviando(true); setErro('');
        try {
            const r = await fetch(urlResponder, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    sessao,
                    // A chave é o id da QUESTÃO DA PROVA (não a do banco) — é lá que
                    // mora o gabarito congelado.
                    respostas: Object.entries(respostas).map(([idProvaQuestao, idAlternativa]) => ({
                        idProvaQuestao: Number(idProvaQuestao), idAlternativa: Number(idAlternativa),
                    })),
                }),
            });
            const d = await r.json();
            if (!r.ok) {
                if (r.status === 410) { setEncerrada(true); return; }
                throw new Error(d.message);
            }
            setEntregue(true);
        } catch (err) { setErro(err.message); }
        finally { setEnviando(false); }
    };

    const pagina = (conteudo) => (
        <div style={{ backgroundColor: '#f1f5f9', minHeight: '100vh', padding: '32px 16px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
            <div style={{ width: '100%', maxWidth: '680px', margin: '0 auto' }}>{conteudo}</div>
        </div>
    );

    const caixa = (filhos, extra = {}) => (
        <div style={{ backgroundColor: '#fff', borderRadius: '16px', padding: '28px', border: '1px solid #e2e8f0', ...extra }}>{filhos}</div>
    );

    if (erroCarga) return pagina(caixa(
        <div style={{ textAlign: 'center' }}>
            <AlertCircle size={48} color="#be123c" />
            <h2 style={{ color: '#0f172a', margin: '12px 0 6px 0' }}>Prova não disponível</h2>
            <p style={{ color: '#64748b', margin: 0 }}>Confira o QR code com o RH.</p>
        </div>
    ));

    // Prova encerrada pelo aplicador — as respostas até aqui já foram salvas.
    if (encerrada) return pagina(caixa(
        <div style={{ textAlign: 'center' }}>
            <CheckCircle2 size={52} color="#059669" />
            <h2 style={{ color: '#0f172a', margin: '12px 0 6px 0' }}>Prova encerrada</h2>
            <p style={{ color: '#475569', margin: 0, lineHeight: 1.6 }}>
                O aplicador encerrou esta prova. <strong>Suas respostas até aqui foram salvas.</strong> O resultado será comunicado pelo RH.
            </p>
        </div>
    ));

    if (entregue) return pagina(caixa(
        <div style={{ textAlign: 'center' }}>
            <CheckCircle2 size={56} color="#059669" />
            <h2 style={{ color: '#0f172a', margin: '14px 0 8px 0' }}>Prova entregue!</h2>
            <p style={{ color: '#475569', margin: 0, lineHeight: 1.6 }}>
                Obrigado, <strong>{nome}</strong>. O resultado será comunicado pelo RH.
            </p>
        </div>
    ));

    if (!cabecalho) return pagina(<p style={{ textAlign: 'center', color: '#64748b' }}>Carregando…</p>);

    // ── Identificação (CPF) ──
    if (!sessao) return pagina(
        <>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <img alt="Grupo Correia" src="/icon-logotipo.png" style={{ height: '90px' }} />
            </div>
            {caixa(
                <>
                    <div style={{ borderLeft: `4px solid ${COR}`, paddingLeft: '14px', marginBottom: '22px' }}>
                        <p style={{ fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', color: COR, margin: 0 }}>
                            {/* Fluxo por VAGA devolve um nome; o da SALA devolve a lista dos
                                processos aplicados juntos (2026-08-24). */}
                            {cabecalho.processos ? cabecalho.processos.join(` · `) : cabecalho.processo}
                        </p>
                        <h1 style={{ fontSize: '22px', fontWeight: 800, color: '#0f172a', margin: '4px 0 0 0' }}>
                            {cabecalho.cargo ? `Prova — ${cabecalho.cargo}` : 'Prova'}
                        </h1>
                        <p style={{ color: '#64748b', fontSize: '14px', margin: '6px 0 0 0' }}>
                            {/* No fluxo por processo, o cargo/quantidade só são conhecidos
                                após o CPF (o QR é único para a sala). */}
                            {cabecalho.qtdQuestoes ? `${cabecalho.qtdQuestoes} questões · ` : ''}sem tempo limite
                        </p>
                    </div>

                    <form onSubmit={iniciar}>
                        <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#475569', marginBottom: '6px' }}>
                            Informe seu CPF para começar
                        </label>
                        <input
                            value={cpf} onChange={(e) => setCpf(mascararCPF(e.target.value))}
                            placeholder="000.000.000-00" inputMode="numeric" required
                            style={{ width: '100%', padding: '14px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '17px', boxSizing: 'border-box', marginBottom: '16px' }}
                        />
                        {erro && (
                            <div style={{ backgroundColor: '#fff1f2', color: '#be123c', padding: '12px', borderRadius: '8px', fontSize: '14px', marginBottom: '16px' }}>{erro}</div>
                        )}
                        <button type="submit" disabled={entrando}
                            style={{ width: '100%', padding: '15px', borderRadius: '8px', border: 'none', backgroundColor: COR, color: '#fff', fontSize: '16px', fontWeight: 700, cursor: entrando ? 'not-allowed' : 'pointer', opacity: entrando ? 0.7 : 1 }}>
                            {entrando ? 'Abrindo…' : 'Começar a prova'}
                        </button>
                    </form>
                </>
            )}
        </>
    );

    // ── A prova (LISTA ÚNICA, sem área; a ordem das questões e das alternativas vem
    //    embaralhada pelo servidor, estável para esta pessoa) ──
    const respondidas = Object.keys(respostas).length;
    return pagina(
        <>
            <div style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#fff', borderRadius: '12px', padding: '14px 18px', border: '1px solid #e2e8f0', marginBottom: '18px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <div>
                        <strong style={{ color: '#0f172a', fontSize: '15px' }}>{nome}</strong>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{cabecalho.cargo}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '13px', fontWeight: 800, color: respondidas === todasQuestoes.length ? '#059669' : COR }}>
                            {respondidas} / {todasQuestoes.length} respondidas
                        </div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>salvo automaticamente</div>
                    </div>
                </div>
            </div>

            {questoes.map((q, i) => (
                <div key={q.id} style={{ backgroundColor: '#fff', borderRadius: '14px', padding: '20px', border: '1px solid #e2e8f0', marginBottom: '14px' }}>
                    <p style={{ fontSize: '15px', fontWeight: 700, color: '#0f172a', margin: '0 0 14px 0', lineHeight: 1.5 }}>
                        <span style={{ color: COR, fontWeight: 900 }}>{i + 1}.</span> {q.texto}
                    </p>
                    {q.alternativas.map((a) => {
                        const marcada = respostas[q.id] === a.id;
                        return (
                            <label key={a.id}
                                style={{
                                    display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 14px',
                                    border: `1px solid ${marcada ? COR : '#e2e8f0'}`, borderRadius: '10px', marginBottom: '8px',
                                    backgroundColor: marcada ? '#e0f2fe' : '#fff', cursor: 'pointer',
                                }}>
                                <input
                                    type="radio" name={`q-${q.id}`} checked={marcada}
                                    onChange={() => marcar(q.id, a.id)}
                                    style={{ marginTop: '3px' }}
                                />
                                <span style={{ fontSize: '14px', color: '#0f172a', lineHeight: 1.5 }}>{a.texto}</span>
                            </label>
                        );
                    })}
                </div>
            ))}

            {erro && (
                <div style={{ backgroundColor: '#fff1f2', color: '#be123c', padding: '12px', borderRadius: '8px', fontSize: '14px', marginBottom: '14px' }}>{erro}</div>
            )}

            <button onClick={enviar} disabled={enviando}
                style={{ width: '100%', padding: '16px', borderRadius: '10px', border: 'none', backgroundColor: COR, color: '#fff', fontSize: '17px', fontWeight: 800, cursor: enviando ? 'not-allowed' : 'pointer', opacity: enviando ? 0.7 : 1, marginBottom: '40px' }}>
                {enviando ? 'Entregando…' : 'Entregar prova'}
            </button>
        </>
    );
}

export default PPIProvaPublica;
